import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import Sidebar from '../main/Sidebar';
import Topbar from '../main/Topbar';

// Utils
import { getUserInfo } from '../../utils/auth';
import { createUser } from '../../utils/user';
import { UserFormUtils, FormData, FormErrors } from '../../utils/userCommon';

import { 
    Save,
    ArrowLeft,
    Shield,
    Eye,
    EyeOff,
    UserPlus,
    User,
    RefreshCw
  } from 'lucide-react';
  
  
  const UserCreate: React.FC = () => {
    const navigate = useNavigate();

    // UI state
    const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState<boolean>(true);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState<boolean>(false);
    const [activeMenu, setActiveMenu] = useState<string>('users');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
  
    const [formData, setFormData] = useState<FormData>(UserFormUtils.getDefaultFormData());
  
    const [errors, setErrors] = useState<FormErrors>({});
  
    useEffect(() => {
      const handleResize = () => {
        if (window.innerWidth >= 1024) {
          setIsMobileSidebarOpen(false);
        }
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
  
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      UserFormUtils.handleInputChange(e, setFormData, setErrors);
    };
  
    const validateForm = (): boolean => {
      const newErrors = UserFormUtils.validateCreateForm(formData);
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    // Reset form
    const handleReset = () => {
      setFormData(UserFormUtils.getDefaultFormData());
      setErrors({});
      setSubmitError(null);
      setShowPassword(false);
      setShowConfirmPassword(false);
    };

    const getAccountCode = (): string | null => {
      const userInfo = getUserInfo();
      return userInfo?.accountCode || null;
    };
  
    const handleSubmit = async (e: React.FormEvent) => {
      await UserFormUtils.handleCreateSubmit(
        e,
        formData,
        validateForm,
        setIsSubmitting,
        setSubmitError,
        getAccountCode,
        createUser,
        navigate
      );
    };
  
    return (
      <div className="h-screen bg-gray-50 flex overflow-hidden">
        <Sidebar
          isDesktopSidebarOpen={isDesktopSidebarOpen}
          setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
        />
  
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <Topbar
            title="사용자 추가"
            subtitle="새로운 사용자를 추가하세요"
            isMobileSidebarOpen={isMobileSidebarOpen}
            setIsMobileSidebarOpen={setIsMobileSidebarOpen}
            isProfileDropdownOpen={isProfileDropdownOpen}
            setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          />
  
          <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
            <div className="max-w-3xl mx-auto">
              {/* Back Button */}
              <div className="mb-6">
                <button 
                  onClick={() => navigate('/users')}
                  className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  사용자 목록으로
                </button>
              </div>

              {/* Error Message */}
              {submitError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">오류가 발생했습니다</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{submitError}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
  
              <form onSubmit={handleSubmit}>
                {/* Account Information */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    계정 정보
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        아이디 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="userId"
                        value={formData.userId}
                        onChange={handleInputChange}
                        maxLength={20}
                        className={`w-full px-3 py-2 border ${errors.userId ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="user123"
                      />
                      {errors.userId && <p className="mt-1 text-xs text-red-500">{errors.userId}</p>}
                    </div>
  
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        상태 <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="ACTIVE">활성</option>
                        <option value="INACTIVE">비활성</option>
                        <option value="suspended">정지</option>
                      </select>
                    </div>
  
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        비밀번호 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          maxLength={20}
                          className={`w-full px-3 py-2 pr-10 border ${errors.password ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          placeholder="8-20자 입력"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5 text-gray-400" />
                          ) : (
                            <Eye className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                      {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
                    </div>
  
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        비밀번호 확인 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          maxLength={20}
                          className={`w-full px-3 py-2 pr-10 border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          placeholder="비밀번호 재입력"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-5 h-5 text-gray-400" />
                          ) : (
                            <Eye className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                      {errors.confirmPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>}
                    </div>
                  </div>
                </div>
  
                {/* Personal Information */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <UserPlus className="w-5 h-5 mr-2" />
                    개인 정보
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        사용자명 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        maxLength={50}
                        className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="홍길동"
                      />
                      {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                    </div>
  
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        부서
                      </label>
                      <input
                        type="text"
                        name="department"
                        value={formData.department}
                        onChange={handleInputChange}
                        maxLength={20}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="영업팀"
                      />
                    </div>
  
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        연락처 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        maxLength={20}
                        className={`w-full px-3 py-2 border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="010-1234-5678"
                      />
                      {errors.phoneNumber && <p className="mt-1 text-xs text-red-500">{errors.phoneNumber}</p>}
                    </div>
                  </div>
                </div>
  
                {/* Security Settings - Hidden for now */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 hidden">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Shield className="w-5 h-5 mr-2" />
                    보안 설정
                  </h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      이중 인증 방식
                    </label>
                    <select
                      name="mfaType"
                      value={formData.mfaType}
                      onChange={handleInputChange}
                      className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="NONE">사용안함</option>
                      <option value="SMS">SMS 인증</option>
                      <option value="EMAIL">이메일 인증</option>
                      <option value="OTP">OTP (Google OTP)</option>
                    </select>
                    <p className="mt-2 text-xs text-gray-500">
                      이중 인증을 설정하면 로그인 시 추가 인증이 필요합니다.
                    </p>
                  </div>
                </div>
  
                {/* Action Buttons */}
                <div className="flex justify-between items-center">
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    초기화
                  </button>
                  
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => navigate('/users')}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
                    >
                      <Save className="w-5 h-5 mr-2" />
                      {isSubmitting ? '생성 중...' : '사용자 추가'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </main>
        </div>
      </div>
    );
  };
  
  export default UserCreate;